import { Component } from '@theme/component';

/** How long to wait for the smooth scroll to settle before re-measuring. */
const SETTLE_TIMEOUT = 800;

/** Drift beyond this is the visitor scrolling, not layout shift. */
const MAX_CORRECTION = 800;

/** Anything under this is close enough to leave alone. */
const MIN_CORRECTION = 1;

/**
 * Scrolls to an anchored section without writing the hash into the URL.
 * Links keep a real `href="#id"` so they still work before this module runs.
 * @extends {Component<{}>}
 */
class RsQuickLinks extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = (event.target);
    const link = target?.closest('[data-anchor-target]');

    if (!(link instanceof HTMLAnchorElement)) return;

    const id = link.dataset.anchorTarget;
    if (!id) return;

    const section = document.getElementById(id);

    // Fall through to the href so a stale anchor still tries to resolve.
    if (!section) return;

    event.preventDefault();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // scrollIntoView honours scroll-margin-top, which clears the sticky header.
    section.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });

    this.#alignAfterSettle(section);

    // Keyboard and screen reader users continue from the section, not the top.
    if (!section.hasAttribute('tabindex')) {
      section.setAttribute('tabindex', '-1');
    }

    // preventScroll avoids a second jump that would ignore scroll-margin.
    section.focus({ preventScroll: true });
  };

  /**
   * The destination is measured once, when the scroll starts. Content still
   * settling above the target moves it mid-animation, so the first click on a
   * fresh page lands short. Re-measure once the scroll ends and close the gap.
   *
   * @param {HTMLElement} section
   */
  #alignAfterSettle(section) {
    let done = false;

    const correct = () => {
      if (done) return;
      done = true;
      window.removeEventListener('scrollend', correct);

      const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
      const drift = section.getBoundingClientRect().top - margin;

      // Already aligned, or the visitor has scrolled somewhere else entirely.
      if (Math.abs(drift) < MIN_CORRECTION || Math.abs(drift) > MAX_CORRECTION) return;

      window.scrollBy({ top: drift, behavior: 'auto' });
    };

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', correct, { once: true });
    }

    // Covers browsers without scrollend, and scrolls too short to fire it.
    setTimeout(correct, SETTLE_TIMEOUT);
  }
}

if (!customElements.get('rs-quick-links')) {
  customElements.define('rs-quick-links', RsQuickLinks);
}
